import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import Dropdown from "@/components/ui/dropdown";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type ExamItemAnalysisResponse,
  type ExamItemAnalysisItem,
  type ExamItemAnalysisParams,
  type ExamListItem,
} from "../../services/reports-extended.api";
import { Question, Approve } from "@/components/icons";

const PAGE_SIZE = 20;

const buildClassificationLabel = (t: (k: string) => string): Record<
  ExamItemAnalysisItem["classification"],
  { label: string; color: string; bg: string }
> => ({
  easy: { label: t("reports.examAnalytics.easy"), color: "#166534", bg: "#dcfce7" },
  moderate: { label: t("reports.examAnalytics.moderate"), color: "#1e40af", bg: "#dbeafe" },
  hard: { label: t("reports.examAnalytics.hard"), color: "#92400e", bg: "#fef3c7" },
  "very-hard": { label: t("reports.examAnalytics.veryHard"), color: "#b91c1c", bg: "#fee2e2" },
});

const ExamAnalyticsReportPage: React.FC = () => {
  const { t } = useTranslation();
  const classificationLabel = useMemo(() => buildClassificationLabel(t), [t]);
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [examID, setExamID] = useState<string>("");
  const [examList, setExamList] = useState<ExamListItem[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    reportsExtendedApi
      .listExamsForReports()
      .then((list) => {
        setExamList(list);
        if (list.length > 0 && !examID) setExamID(list[0].id);
      })
      .catch(() => setExamList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params: ExamItemAnalysisParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      examID: examID || undefined,
      search: filter.search?.trim() || undefined,
    }),
    [examID, filter.search, page],
  );

  const { data, isLoading, error } = useReportData<
    ExamItemAnalysisResponse,
    ExamItemAnalysisParams
  >({
    fetcher: (p, signal) => reportsExtendedApi.getExamItemAnalysis(p, signal),
    params,
    enabled: !!examID,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const examOptions = examList.map((e) => ({
    label: `${e.title} (${e.questionCount} soru)`,
    value: e.id,
  }));

  const columns: TableColumn<ExamItemAnalysisItem>[] = [
    {
      key: "questionText",
      label: "SORU",
      header: "SORU",
      accessor: (row) => (
        <span
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            maxWidth: 400,
          }}
          title={row.questionText}
        >
          {row.questionText}
        </span>
      ),
    },
    { header: "DENEME", accessor: (row) => row.attempts },
    { header: t("reports.examAnalytics.headerCorrect"), accessor: (row) => row.correctCount },
    {
      key: "correctRate",
      label: t("reports.examAnalytics.headerSuccessPct"),
      header: t("reports.examAnalytics.headerSuccessPct"),
      accessor: (row) => `${row.correctRate.toFixed(1)}%`,
    },
    {
      key: "difficultyIndex",
      label: "ZORLUK",
      header: "ZORLUK",
      accessor: (row) => row.difficultyIndex.toFixed(2),
    },
    {
      key: "discriminationIndex",
      label: "AYIRICILIK",
      header: "AYIRICILIK",
      accessor: (row) => (
        <span
          style={{
            fontWeight: 600,
            color:
              row.discriminationIndex >= 0.3
                ? "#15803d"
                : row.discriminationIndex >= 0.2
                  ? "#b45309"
                  : "#b91c1c",
          }}
        >
          {row.discriminationIndex.toFixed(2)}
        </span>
      ),
    },
    {
      key: "classification",
      label: "SINIFLANDIRMA",
      header: "SINIFLANDIRMA",
      accessor: (row) => {
        const c = classificationLabel[row.classification];
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 8px",
              borderRadius: 999,
              background: c.bg,
              color: c.color,
              fontWeight: 600,
              fontSize: 11.5,
            }}
          >
            {c.label}
          </span>
        );
      },
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<ExamItemAnalysisItem>({
    reportKey: "EXAM_ITEM_ANALYSIS",
    reportTitle: t("reports.examAnalytics.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.examAnalytics.title")}
      layoutKey={`report:EXAM_ITEM_ANALYSIS`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.quality.title")}
      description={t("reports.examAnalytics.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="EXAM_ITEM_ANALYSIS"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="sinav-soru-analizi"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading || !examID}
        />
        </>
      }
      filterSlot={
        <div style={{ display: "grid", gap: 12 }}>
          <Dropdown
            label={t("reports.examAnalytics.examLabel")}
            placeholder={t("reports.examAnalytics.examPlaceholder")}
            options={examOptions}
            value={examID}
            selectedLabel={
              examOptions.find((o) => o.value === examID)?.label ?? ""
            }
            onSelect={(v) => {
              setExamID(Array.isArray(v) ? (v[0] ?? "") : v);
              setPage(1);
            }}
            searchable
          />
          <ReportFilterBar
            value={filter}
            onChange={(next) => {
              setFilter(next);
              setPage(1);
            }}
            showDateRange={false}
            showDepartment={false}
          />
        </div>
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title="Toplam Soru"
          value={summary?.totalQuestions ?? 0}
          icon={<Question width={18} height={18} />}
        />
        <ReportKPICard
          title="Toplam Deneme"
          value={summary?.totalAttempts ?? 0}
          icon={<Approve width={18} height={18} />}
        />
        <ReportKPICard
          title="Ort. Zorluk (p)"
          value={summary ? summary.avgDifficulty.toFixed(2) : "—"}
          icon={<Question width={18} height={18} />}
        />
        <ReportKPICard
          title="Problemli Soru"
          value={summary?.problematicCount ?? 0}
          icon={<Question width={18} height={18} />}
          accent="danger"
        />
      </div>

      <ReportChart
        title={t("reports.examAnalytics.difficultyVsDiscriminationTitle")}
        subtitle={t("reports.examAnalytics.difficultyVsDiscriminationSubtitle")}
        type="scatter"
        isLoading={isLoading}
        isEmpty={items.length === 0}
        series={[
          {
            name: "Soru",
            data: items.map((i) => [i.difficultyIndex, i.discriminationIndex]),
          },
        ]}
        options={{
          xaxis: {
            title: { text: t("reports.examAnalytics.difficultyAxis") },
            min: 0,
            max: 1,
            tickAmount: 5,
          },
          yaxis: {
            title: { text: t("reports.examAnalytics.discriminationAxis") },
            min: -0.2,
            max: 1,
          },
          colors: ["#4f46e5"],
        }}
      />

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.questionID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={error ? t("reports.examAnalytics.noData") : t("reports.examAnalytics.noQuestion")}
      />
    </ReportDetailLayout>
  );
};

export default ExamAnalyticsReportPage;
